import { useState } from 'react'
import { ClassTree } from './ClassTree'
import { ClassDetail } from './ClassDetail'
import { ALL_CLASSES } from '@/lib/ontology/schema'
import type { OntologyClass, OntologyDomain } from '@/types/ontology'
import { Card } from '@/components/ui/card'
import { Globe, Building2, Puzzle, Layers, TrendingUp } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SchemaBrowserProps {
  filterDomain?: OntologyDomain | null
}

export function SchemaBrowser({ filterDomain }: SchemaBrowserProps) {
  const [selectedClass, setSelectedClass] = useState<OntologyClass | null>(null)

  const filteredClasses = filterDomain
    ? ALL_CLASSES.filter((c) => c.domain === filterDomain)
    : ALL_CLASSES

  const stats = {
    total: filteredClasses.length,
    market: filteredClasses.filter((c) => c.domain === 'market').length,
    business: filteredClasses.filter((c) => c.domain === 'business').length,
    solution: filteredClasses.filter((c) => c.domain === 'solution').length,
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', flexShrink: 0 }}>
        <StatCard
          icon={Layers}
          iconBg="bg-warm-100"
          iconColor="text-warm-600"
          value={stats.total}
          label="Total Classes"
        />
        <StatCard
          icon={Globe}
          iconBg="bg-market/10"
          iconColor="text-market"
          value={stats.market}
          label="Market Domain"
          trend="+2"
        />
        <StatCard
          icon={Building2}
          iconBg="bg-business/10"
          iconColor="text-business"
          value={stats.business}
          label="Business Domain"
          trend="+5"
        />
        <StatCard
          icon={Puzzle}
          iconBg="bg-solution/10"
          iconColor="text-solution"
          value={stats.solution}
          label="Solution Domain"
          trend="+3"
        />
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, display: 'flex', gap: '24px', minHeight: 0 }}>
        {/* Left Panel - Class Tree */}
        <Card style={{ width: '320px', flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
            <ClassTree
              classes={ALL_CLASSES}
              selectedClass={selectedClass}
              onSelectClass={setSelectedClass}
              filterDomain={filterDomain}
            />
          </div>
        </Card>

        {/* Right Panel - Class Detail */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedClass ? (
            <ClassDetail ontologyClass={selectedClass} />
          ) : (
            <Card style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', maxWidth: '320px' }}>
                <div style={{ width: '64px', height: '64px', borderRadius: '16px', backgroundColor: 'var(--color-warm-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <Layers style={{ width: '32px', height: '32px', color: 'var(--color-warm-500)' }} />
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-text)', marginBottom: '8px' }}>Select a Class</h3>
                <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                  Choose a class from the tree to view its details, properties, and relationships.
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

function StatCard({
  icon: Icon,
  iconBg,
  iconColor,
  value,
  label,
  trend
}: {
  icon: React.ComponentType<{ className?: string }>
  iconBg: string
  iconColor: string
  value: number
  label: string
  trend?: string
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between mb-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', iconBg)}>
          <Icon className={cn('w-5 h-5', iconColor)} />
        </div>
        {trend && (
          <span className="flex items-center gap-1 text-xs font-medium text-business">
            <TrendingUp className="w-3 h-3" />
            {trend}
          </span>
        )}
      </div>
      <p className="text-2xl font-bold text-text mb-1">{value}</p>
      <p className="text-sm text-text-muted">{label}</p>
    </Card>
  )
}
